import { InlineFeedback, useFeedback } from '@mts/ui/feedback';

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { createPublicInvoiceCheckout, fetchPublicInvoice } from '../services/api';

function money(cents, currency = 'USD') {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format((Number(cents) || 0) / 100);
}

function date(value) {
  return value ? new Date(value).toLocaleDateString('en-US') : '—';
}

export default function PublicInvoicePage() {
  const toast = useFeedback();
  const { invoiceToken } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetchPublicInvoice(invoiceToken)
      .then((response) => {
        if (active) setInvoice(response.invoice);
      })
      .catch(() => {
        if (active) setError('This invoice is unavailable.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [invoiceToken]);

  async function payNow(paymentOption = 'FULL_BALANCE') {
    setPaying(true);
    setError('');
    try {
      const result = await createPublicInvoiceCheckout(invoiceToken, paymentOption);
      if (!result.checkoutUrl) throw new Error('Checkout is unavailable.');
      window.location.assign(result.checkoutUrl);
    } catch (err) {
      toast.error(err.message || 'Unable to start checkout.', { id: 'action:PublicInvoicePage:payNow' });
      setPaying(false);
    }
  }

  if (loading) return <div className="mx-auto max-w-4xl px-6 py-20 text-center text-gray-500">Loading invoice…</div>;
  if (!invoice) return <div className="mx-auto max-w-4xl px-6 py-20 text-center"><h1 className="text-2xl font-semibold">Invoice unavailable</h1><InlineFeedback variant="error" className="mt-2 text-gray-600">{error}</InlineFeedback></div>;

  return (
    <main className="mx-auto max-w-4xl px-4 py-10 sm:px-6">
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <header className="flex flex-col justify-between gap-6 border-b border-gray-200 p-6 sm:flex-row sm:p-8">
          <div className="flex items-center gap-4">
            {invoice.tenant.logoUrl && <img className="h-14 w-14 rounded object-contain" src={invoice.tenant.logoUrl} alt="" />}
            <div><p className="text-sm text-gray-500">Invoice from</p><h1 className="text-2xl font-semibold text-gray-900">{invoice.tenant.name}</h1></div>
          </div>
          <div className="sm:text-right"><p className="text-sm uppercase tracking-wide text-gray-500">Invoice</p><p className="text-xl font-semibold">{invoice.invoiceNumber}</p><span className="mt-2 inline-block rounded-full bg-gray-100 px-3 py-1 text-xs font-medium">{invoice.status}</span></div>
        </header>

        <section className="grid gap-6 border-b border-gray-200 p-6 sm:grid-cols-3 sm:p-8">
          <div><p className="text-xs uppercase text-gray-500">Bill to</p><p className="mt-1 font-medium">{invoice.customer.name || invoice.customer.companyName || 'Customer'}</p>{invoice.customer.companyName && invoice.customer.companyName !== invoice.customer.name && <p className="text-sm text-gray-600">{invoice.customer.companyName}</p>}</div>
          <div><p className="text-xs uppercase text-gray-500">Issue date</p><p className="mt-1 font-medium">{date(invoice.issueDate)}</p></div>
          <div><p className="text-xs uppercase text-gray-500">Due date</p><p className="mt-1 font-medium">{date(invoice.dueDate)}</p></div>
        </section>

        <section className="overflow-x-auto p-6 sm:p-8">
          <table className="w-full min-w-[620px] text-left text-sm">
            <thead className="border-b text-xs uppercase text-gray-500"><tr><th className="pb-3">Item</th><th className="pb-3 text-right">Qty</th><th className="pb-3 text-right">Unit price</th><th className="pb-3 text-right">Total</th></tr></thead>
            <tbody className="divide-y">
              {invoice.lineItems.map((item, index) => <tr key={index}><td className="py-4 pr-4"><p className="font-medium">{item.name}</p>{item.description && <p className="mt-1 text-gray-500">{item.description}</p>}{item.discountAmountCents > 0 && <p className="mt-1 text-xs text-gray-500">Discount: {money(item.discountAmountCents, invoice.currency)}</p>}{item.taxAmountCents > 0 && <p className="text-xs text-gray-500">Tax: {money(item.taxAmountCents, invoice.currency)}</p>}</td><td className="py-4 text-right">{item.quantity}</td><td className="py-4 text-right">{money(item.unitAmountCents, invoice.currency)}</td><td className="py-4 text-right font-medium">{money(item.totalAmountCents, invoice.currency)}</td></tr>)}
            </tbody>
          </table>
        </section>

        <section className="grid gap-8 border-t border-gray-200 p-6 sm:grid-cols-2 sm:p-8">
          <div className="space-y-5">
            {invoice.notes && <div><h2 className="text-sm font-semibold">Notes</h2><p className="mt-1 whitespace-pre-wrap text-sm text-gray-600">{invoice.notes}</p></div>}
            {invoice.offlinePaymentInstructions && <div><h2 className="text-sm font-semibold">Offline payment</h2><p className="mt-1 whitespace-pre-wrap text-sm text-gray-600">{invoice.offlinePaymentInstructions}</p></div>}
            <div><h2 className="text-sm font-semibold">Contact</h2><p className="mt-1 text-sm text-gray-600">{[invoice.tenant.email, invoice.tenant.phone].filter(Boolean).join(' · ') || 'Contact the seller for assistance.'}</p></div>
            {invoice.pdfAvailable
              ? <a className="inline-block rounded border border-gray-300 px-3 py-2 text-sm" href={`/api/public/commerce/invoices/${encodeURIComponent(invoiceToken)}/pdf`}>Download PDF</a>
              : <span className="inline-block rounded border border-gray-200 px-3 py-2 text-sm text-gray-400">PDF is being prepared</span>}
          </div>
          <div>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between"><dt>Subtotal</dt><dd>{money(invoice.subtotalAmountCents, invoice.currency)}</dd></div>
              {invoice.discountAmountCents > 0 && <div className="flex justify-between"><dt>Discount</dt><dd>−{money(invoice.discountAmountCents, invoice.currency)}</dd></div>}
              {invoice.taxAmountCents > 0 && <div className="flex justify-between"><dt>Tax</dt><dd>{money(invoice.taxAmountCents, invoice.currency)}</dd></div>}
              <div className="flex justify-between border-t pt-2 font-medium"><dt>Total</dt><dd>{money(invoice.totalAmountCents, invoice.currency)}</dd></div>
              <div className="flex justify-between"><dt>Payments received</dt><dd>−{money(invoice.amountPaidCents, invoice.currency)}</dd></div>
              {invoice.minimumInitialPaymentAmountCents > 0 && <div className="flex justify-between"><dt>Required deposit</dt><dd>{money(invoice.minimumInitialPaymentAmountCents, invoice.currency)}</dd></div>}
              {invoice.minimumInitialPaymentAmountCents > 0 && <div className="flex justify-between"><dt>Deposit paid</dt><dd>{money(invoice.depositPaidAmountCents, invoice.currency)}</dd></div>}
              <div className="flex justify-between border-t pt-3 text-lg font-semibold"><dt>Balance due</dt><dd>{money(invoice.balanceAmountCents, invoice.currency)}</dd></div>
            </dl>
            {invoice.canPay ? (
              <div className="mt-6 space-y-3">
                {!invoice.depositSatisfied && invoice.amountPaidCents === 0 && <button disabled={paying} onClick={() => payNow('MINIMUM_DUE')} className="w-full rounded-lg bg-gray-900 px-4 py-3 font-medium text-white disabled:opacity-60">{paying ? 'Opening checkout…' : `Pay deposit ${money(invoice.minimumInitialPaymentAmountCents, invoice.currency)}`}</button>}
                <button disabled={paying} onClick={() => payNow('FULL_BALANCE')} className={`w-full rounded-lg px-4 py-3 font-medium disabled:opacity-60 ${!invoice.depositSatisfied ? 'border border-gray-300 bg-white text-gray-900' : 'bg-gray-900 text-white'}`}>{paying ? 'Opening checkout…' : `${invoice.amountPaidCents > 0 ? 'Pay remaining balance' : 'Pay in full'} ${money(invoice.balanceAmountCents, invoice.currency)}`}</button>
              </div>
            ) : <p className="mt-6 rounded-lg bg-emerald-50 p-3 text-center text-sm font-medium text-emerald-700">{invoice.status === 'PAID' ? 'Paid in full' : 'Payment is unavailable'}</p>}
            {error && <InlineFeedback variant="error" className="mt-3 text-sm text-rose-700">{error}</InlineFeedback>}
          </div>
        </section>
      </div>
    </main>
  );
}
